import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Globe,
  Hash,
  Info,
  Loader2,
  Mail,
  Pencil,
  Shield,
  UserCircle,
  X,
  XCircle,
} from 'lucide-react';
import { api, type X5User, type X5UserAuthEvent, type X5UserIdentityDetail } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { Badge } from './ui/Badge';
import { useOrgLookup } from '../lib/orgLookup';
import { formatDateTime, formatRelative } from '../lib/format';
import { useAuth } from '../contexts/use-auth';

interface Props {
  user: X5User;
  onClose: () => void;
}

/**
 * Everything known about one X5 customer: the Amplify row, the matching Cognito
 * identity, and that identity's sign-in history. Shared by the Users list and
 * the Organizations detail page so a user reads the same way from either.
 */
export function UserDetailDrawer({ user, onClose }: Props) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('x5.org.members');
  const lookup = useOrgLookup();
  const org = user.organizationId ? lookup.byId.get(user.organizationId) : undefined;
  const organizationName = lookup.resolveName(user.organizationId);
  const orgOptions = useMemo(
    () =>
      Array.from(lookup.byId.values()).sort((a, b) =>
        (a.name ?? a.company ?? a.id).localeCompare(b.name ?? b.company ?? b.id),
      ),
    [lookup.byId],
  );

  const [editing, setEditing] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(user.organizationId ?? '');
  const [editError, setEditError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateUser(user.id, {
        organizationId: selectedOrgId === '' ? null : selectedOrgId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
      setEditing(false);
      setEditError(null);
    },
    onError: (err) => setEditError((err as Error).message),
  });

  const identity = useQuery({
    queryKey: ['users', 'identity', user.username ?? ''],
    queryFn: () => api.getUserIdentity(user.username!),
    enabled: !!user.username,
    staleTime: 60 * 1000,
  });

  const raw = user as unknown as Record<string, unknown>;
  const version = typeof raw['_version'] === 'number' ? raw['_version'] : null;
  const lastChangedAtMs =
    typeof raw['_lastChangedAt'] === 'number' ? raw['_lastChangedAt'] : null;
  const lastChangedAtIso = lastChangedAtMs ? new Date(lastChangedAtMs).toISOString() : null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-muted-foreground" />
              {user.username ?? '(no username)'}
            </h2>
            <p className="text-xs text-muted-foreground font-mono truncate">{user.id}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-5">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  X5 record
                </CardTitle>
                <CardDescription>
                  Fields stored in the Amplify-backed{' '}
                  <code className="font-mono text-xs">User</code> table.
                </CardDescription>
              </div>
              {canEdit && !editing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedOrgId(user.organizationId ?? '');
                    setEditError(null);
                    setEditing(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditing(false);
                      setEditError(null);
                    }}
                    disabled={saveMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveMutation.mutate()}
                    disabled={
                      saveMutation.isPending || selectedOrgId === (user.organizationId ?? '')
                    }
                  >
                    {saveMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Row
                  label="Username"
                  value={<code className="font-mono">{user.username ?? '—'}</code>}
                />
                <Row label="User ID" value={<code className="font-mono text-xs">{user.id}</code>} />
                <Row
                  label="Organization"
                  value={
                    editing ? (
                      <Select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                        className="w-full"
                      >
                        <option value="">— None —</option>
                        {orgOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name ?? o.company ?? o.id}
                          </option>
                        ))}
                      </Select>
                    ) : user.organizationId ? (
                      <Link
                        to={`/organizations/${user.organizationId}`}
                        className="text-primary hover:underline inline-flex items-center gap-1.5"
                        onClick={onClose}
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        {organizationName}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Organization ID"
                  value={
                    <code className="font-mono text-xs">
                      {editing ? selectedOrgId || '(none)' : (user.organizationId ?? '—')}
                    </code>
                  }
                />
                {org?.company && org.company !== org.name && (
                  <Row label="Company" value={org.company} />
                )}
                <Row
                  label="Created"
                  value={
                    user.createdAt ? (
                      <span title={user.createdAt}>
                        {formatRelative(user.createdAt)} · {formatDateTime(user.createdAt)}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Updated"
                  value={
                    user.updatedAt ? (
                      <span title={user.updatedAt}>
                        {formatRelative(user.updatedAt)} · {formatDateTime(user.updatedAt)}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Last Amplify change"
                  value={
                    lastChangedAtIso ? (
                      <span title={lastChangedAtIso}>{formatRelative(lastChangedAtIso)}</span>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row
                  label="Amplify version"
                  value={
                    version != null ? (
                      <Badge variant="outline" className="text-[10px]">
                        v{version}
                      </Badge>
                    ) : (
                      '—'
                    )
                  }
                />
              </dl>
            </CardContent>
          </Card>

          {editError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3">
              {editError}
            </div>
          )}

          {!user.organizationId && !editing && (
            <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">No organization assigned</p>
                <p className="text-xs mt-1 opacity-90">
                  This user has no <code className="font-mono">organizationId</code>.
                  {canEdit && ' Use the Edit button above to assign one.'}
                </p>
              </div>
            </div>
          )}

          <IdentitySection identityQuery={identity} />
        </div>
      </div>
    </div>
  );
}

function IdentitySection({
  identityQuery,
}: {
  identityQuery: ReturnType<typeof useQuery<X5UserIdentityDetail>>;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-muted-foreground" />
          Identity (Cognito · xiltrix_customer)
        </CardTitle>
        <CardDescription>
          Looked up by username against the customer Cognito user pool.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {identityQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Looking up identity record…
          </div>
        )}
        {identityQuery.error && (
          <div className="text-sm text-destructive">{(identityQuery.error as Error).message}</div>
        )}
        {identityQuery.data && !identityQuery.data.exists && (
          <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">No Cognito record</p>
              <p className="text-xs mt-1 opacity-90">
                This username doesn't exist in the customer Cognito pool. The user may have been
                created in the X5 database but never finished sign-up, or this row is stale.
              </p>
            </div>
          </div>
        )}
        {identityQuery.data && identityQuery.data.exists && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={identityQuery.data.status === 'CONFIRMED' ? 'success' : 'outline'}>
                {identityQuery.data.status ?? 'UNKNOWN'}
              </Badge>
              {identityQuery.data.enabled === false && <Badge variant="destructive">DISABLED</Badge>}
              {identityQuery.data.attributes['email_verified'] === 'true' && (
                <Badge variant="outline" className="text-[10px]">
                  email verified
                </Badge>
              )}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {identityQuery.data.attributes['email'] && (
                <Row
                  label="Email"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      <a
                        href={`mailto:${identityQuery.data.attributes['email']}`}
                        className="text-primary hover:underline"
                      >
                        {identityQuery.data.attributes['email']}
                      </a>
                    </span>
                  }
                />
              )}
              <Row
                label="Cognito sub"
                value={
                  <code className="font-mono text-xs">
                    {identityQuery.data.attributes['sub'] ?? '—'}
                  </code>
                }
              />
              <Row
                label="First seen (Cognito)"
                value={
                  <span title={identityQuery.data.userCreateDate ?? ''}>
                    {identityQuery.data.userCreateDate
                      ? `${formatRelative(identityQuery.data.userCreateDate)} · ${formatDateTime(identityQuery.data.userCreateDate)}`
                      : '—'}
                  </span>
                }
              />
              <Row
                label="Last activity"
                value={
                  <span title={identityQuery.data.userLastModifiedDate ?? ''}>
                    {identityQuery.data.userLastModifiedDate
                      ? `${formatRelative(identityQuery.data.userLastModifiedDate)} · ${formatDateTime(identityQuery.data.userLastModifiedDate)}`
                      : '—'}
                  </span>
                }
              />
              {identityQuery.data.attributes['name'] && (
                <Row label="Name" value={identityQuery.data.attributes['name']} />
              )}
              {identityQuery.data.attributes['preferred_username'] && (
                <Row
                  label="Preferred username"
                  value={
                    <code className="font-mono text-xs">
                      {identityQuery.data.attributes['preferred_username']}
                    </code>
                  }
                />
              )}
              {identityQuery.data.attributes['phone_number'] && (
                <Row label="Phone" value={identityQuery.data.attributes['phone_number']} />
              )}
            </dl>

            <AuthEventsSection
              events={identityQuery.data.authEvents}
              error={identityQuery.data.authEventsError}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AuthEventsSection({ events, error }: { events: X5UserAuthEvent[]; error: string | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
        <Shield className="h-3.5 w-3.5" />
        Auth history
      </div>
      {error && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
      {!error && events.length === 0 && (
        <p className="text-xs text-muted-foreground">No auth events recorded.</p>
      )}
      {events.length > 0 && (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-y border-border">
              <tr>
                <th className="px-6 py-2 font-medium text-left">When</th>
                <th className="px-3 py-2 font-medium text-left">Event</th>
                <th className="px-3 py-2 font-medium text-left">Result</th>
                <th className="px-3 py-2 font-medium text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((e) => (
                <tr key={e.eventId ?? `${e.creationDate}-${e.eventType}`}>
                  <td className="px-6 py-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span title={e.creationDate ?? ''}>{formatRelative(e.creationDate)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono">{e.eventType ?? '—'}</td>
                  <td className="px-3 py-2">
                    {e.eventResponse === 'Pass' ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CheckCircle2 className="h-3 w-3" />
                        {e.eventResponse}
                      </span>
                    ) : e.eventResponse ? (
                      <span className="inline-flex items-center gap-1 text-destructive">
                        <XCircle className="h-3 w-3" />
                        {e.eventResponse}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      {e.ipAddress && <span className="font-mono text-[11px]">{e.ipAddress}</span>}
                      {(e.city || e.country) && (
                        <span className="inline-flex items-center gap-1 text-[11px]">
                          <Globe className="h-3 w-3" />
                          {[e.city, e.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {e.deviceName && <span className="text-[11px] opacity-80">{e.deviceName}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}
